import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Bell,
  Search,
  LogOut,
  Settings,
  LayoutDashboard,
  Users,
  Users2,
  MapPin,
  MessageSquare,
  AlertTriangle,
  User,
  ChevronDown,
  CheckCircle,
  XCircle,
  Info,
  AlertCircle as AlertCircleIcon,
  Navigation,
  Music,
  BookOpen,
  Menu,
  X,
  Battery,
  Zap,
  Route,
  CheckCircle2,
  Crown,
  Activity,
  Smartphone,
  Laptop,
  Key,
  TrendingUp,
  Send
} from 'lucide-react';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('https://api.lifeshare.app/api/v1/admin/notifications');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Notification fetch error:', error);
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await axios.post(`https://api.lifeshare.app/api/v1/admin/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Mark read error:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post('https://api.lifeshare.app/api/v1/admin/notifications/read-all');
      fetchNotifications();
    } catch (error) {
      console.error('Mark all read error:', error);
    }
  };

  const getTypeConfig = (type: string) => {
    switch (type) {
      case 'arrival': return { icon: <MapPin className="w-3.5 h-3.5 text-white" />, bg: 'bg-emerald-500' };
      case 'departure': return { icon: <Navigation className="w-3.5 h-3.5 text-white" />, bg: 'bg-rose-500' };
      case 'low_battery': return { icon: <Battery className="w-3.5 h-3.5 text-white" />, bg: 'bg-orange-500' };
      case 'speed_alert': return { icon: <Zap className="w-3.5 h-3.5 text-white" />, bg: 'bg-amber-500' };
      case 'trip_completed': return { icon: <CheckCircle2 className="w-3.5 h-3.5 text-white" />, bg: 'bg-blue-500' };
      case 'route_deviation': return { icon: <Route className="w-3.5 h-3.5 text-white" />, bg: 'bg-violet-500' };
      case 'success': return { icon: <CheckCircle className="w-3.5 h-3.5 text-white" />, bg: 'bg-green-500' };
      case 'error': return { icon: <XCircle className="w-3.5 h-3.5 text-white" />, bg: 'bg-red-500' };
      case 'warning': return { icon: <AlertCircleIcon className="w-3.5 h-3.5 text-white" />, bg: 'bg-orange-500' };
      default: return { icon: <Bell className="w-3.5 h-3.5 text-white" />, bg: 'bg-purple-500' };
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const ts = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 0) return 'Az önce';
    if (diff < 60) return 'Az önce';
    if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} sa`;
    return `${Math.floor(diff / 86400)} gün`;
  };

  const getPageInfo = () => {
    const path = location.pathname;
    const pages: Record<string, { title: string; icon: React.ElementType }> = {
      '/': { title: 'Genel Bakış', icon: LayoutDashboard },
      '/dashboard': { title: 'Genel Bakış', icon: LayoutDashboard },
      '/broadcast': { title: 'Toplu Bildirim', icon: Send },
      '/analytics': { title: 'Analizler', icon: TrendingUp },
      '/users': { title: 'Kullanıcılar', icon: Users },
      '/password-resets': { title: 'Şifre Sıfırlama', icon: Key },
      '/sessions': { title: 'Oturumlar', icon: Laptop },
      '/devices': { title: 'Cihazlar', icon: Smartphone },
      '/user-statuses': { title: 'Durumlar', icon: Activity },
      '/subscriptions': { title: 'Abonelikler', icon: Crown },
      '/families': { title: 'Çevreler', icon: Users2 },
      '/places': { title: 'Yerler', icon: MapPin },
      '/messages': { title: 'Mesajlar', icon: MessageSquare },
      '/emergencies': { title: 'Acil Durumlar', icon: AlertTriangle },
      '/live-map': { title: 'Canlı Harita', icon: Navigation },
      '/stories': { title: 'Hikayeler', icon: BookOpen },
      '/music': { title: 'Müzik', icon: Music },
      '/trips': { title: 'Yolculuklar', icon: Navigation },
      '/settings': { title: 'Ayarlar', icon: Settings },
    };
    return pages[path] || { title: 'Admin Panel', icon: LayoutDashboard };
  };

  const { title, icon: PageIcon } = getPageInfo();

  return (
    <>
      {/* Main Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
        <div className="px-4 md:px-6">
          <div className="flex items-center justify-between h-16">

            {/* Left: Page Title */}
            <div className="flex items-center gap-3 ml-12 lg:ml-0 min-w-0 flex-1">
              <div className="p-2 bg-purple-100 rounded-xl shrink-0 hidden min-[380px]:block">
                <PageIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-gray-900 truncate" title={title}>{title}</h1>
                <p className="text-xs text-gray-500 hidden sm:block">LifeShare Admin</p>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 shrink-0">

              {/* Search Button (Mobile) */}
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors md:hidden"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Search (Desktop) */}
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-56 pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {showNotifications && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)} />
                    <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-[400px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-20 max-h-[80vh] sm:max-h-[480px] overflow-hidden flex flex-col">
                      {/* Header */}
                      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">Bildirimler</h3>
                          {unreadCount > 0 && (
                            <p className="text-xs text-gray-500 mt-0.5">{unreadCount} okunmamış</p>
                          )}
                        </div>
                        {unreadCount > 0 && (
                          <button onClick={markAllAsRead} className="px-3 py-1.5 text-xs bg-purple-50 text-purple-600 hover:bg-purple-100 rounded-full font-medium transition-colors">
                            Tümünü oku
                          </button>
                        )}
                      </div>

                      {/* Notifications List */}
                      <div className="overflow-y-auto flex-1">
                        {notifications.length > 0 ? (
                          notifications.slice(0, 10).map((n) => {
                            const typeConf = getTypeConfig(n.notification_type || n.type);
                            return (
                            <div
                              key={n.id}
                              onClick={() => !n.is_read && markAsRead(n.id)}
                              className={`px-5 py-3.5 hover:bg-gray-50 cursor-pointer transition-colors ${!n.is_read ? 'bg-purple-50/50' : ''}`}
                            >
                              <div className="flex items-start gap-3">
                                {/* Avatar + type badge */}
                                <div className="relative flex-shrink-0">
                                  {n.user_avatar ? (
                                    <img src={`https://api.lifeshare.app${n.user_avatar}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                      <span className="text-purple-600 text-sm font-semibold">{(n.user_username || n.title || '?').charAt(0).toUpperCase()}</span>
                                    </div>
                                  )}
                                  <div className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 ${typeConf.bg} rounded-full flex items-center justify-center ring-2 ring-white`}>
                                    {typeConf.icon}
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <h4 className="text-sm font-semibold text-gray-900 truncate">{n.title}</h4>
                                    {!n.is_read && <div className="w-2 h-2 bg-purple-600 rounded-full flex-shrink-0" />}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                                  <div className="flex items-center gap-2 mt-1.5">
                                    {n.user_username && <span className="text-[11px] text-gray-400">@{n.user_username}</span>}
                                    <span className="text-[11px] text-gray-400">{getTimeAgo(n.created_at)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            );
                          })
                        ) : (
                          <div className="p-10 text-center text-gray-500">
                            <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                              <Bell className="w-6 h-6 text-gray-400" />
                            </div>
                            <p className="text-sm font-medium">Henüz bildirim yok</p>
                            <p className="text-xs text-gray-400 mt-1">Yeni bildirimler burada görünecek</p>
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      {notifications.length > 0 && (
                        <div className="px-5 py-3 border-t border-gray-100">
                          <button
                            onClick={() => { navigate('/notifications'); setShowNotifications(false); }}
                            className="w-full py-2 text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-xl transition-colors"
                          >
                            Tümünü Gör
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Settings */}
              <button
                onClick={() => navigate('/settings')}
                className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors hidden md:flex"
              >
                <Settings className="w-5 h-5" />
              </button>

              {/* User Menu */}
              <div className="relative ml-2 pl-3 border-l border-gray-200">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 hover:bg-gray-50 rounded-xl p-1.5 transition"
                >
                  {user?.avatar_url ? (
                    <img
                      src={`https://api.lifeshare.app${user.avatar_url}`}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover ring-2 ring-purple-100"
                    />
                  ) : (
                    <div className="w-9 h-9 bg-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                      {user?.username?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-[100px]">{user?.full_name || user?.username}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.admin_role?.replace('_', ' ')}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50">

                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.username}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>

                      <div className="py-1">
                        <button
                          onClick={() => { navigate('/profile'); setShowUserMenu(false); }}
                          className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                        >
                          <User className="w-4 h-4" />
                          <span className="text-sm">Profil</span>
                        </button>
                        <button
                          onClick={() => { navigate('/settings'); setShowUserMenu(false); }}
                          className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                        >
                          <Settings className="w-4 h-4" />
                          <span className="text-sm">Ayarlar</span>
                        </button>
                      </div>

                      <div className="border-t border-gray-100 pt-1">
                        <button
                          onClick={() => { logout(); setShowUserMenu(false); }}
                          className="w-full px-4 py-2.5 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                        >
                          <LogOut className="w-4 h-4" />
                          <span className="text-sm font-medium">Çıkış Yap</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Search Bar */}
      {showSearch && (
        <div className="fixed inset-x-0 top-16 z-30 bg-white border-b border-gray-200 p-4 md:hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Ara..."
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button onClick={() => setShowSearch(false)} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
